import { ForwardedRef, forwardRef, MutableRefObject, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { AgGridReact } from 'ag-grid-react'
import 'ag-grid-enterprise'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-balham.css'
import { agGridOptions, defaultColDef, gridOptions as defaultGridOptions } from '@/components/table/grid_table_options'
import { cloneDeep } from 'lodash-es'
import { GridTableV2Props } from '@/components/tableV2/gridV2_types'
import {
  CellValueChangedEvent,
  FilterChangedEvent,
  GetRowIdParams,
  ICellRendererParams,
  RowClassParams,
  RowDoubleClickedEvent,
  RowStyle,
  SelectionChangedEvent,
} from 'ag-grid-community'
import {
  AgGridReactWrapV2,
  GridTableV2Wrap,
  GridTitleWrap,
  GridTableHeaderWrap,
  GridTableHeaderLeft,
  TableToolbarWrap,
} from '@/components/tableV2/gridV2_styles'
import { useCheckedNum } from '@/components/table/GridTable.atoms'
import { SearchBar, Divider, ExportButton, UploadButton, DeleteButton, AddButton, Pagination } from '@/components/tableV2/GridTableV2Toolbar'
import { NoRowsOverlayComponent } from './NoRowsOverlayComponenet'
import { Typography } from '@musinsa/layer-components-desktop'
import { tokens } from '@musinsa/layer-tokens/desktop'
import { useTableColumnDefs } from '@/hooks/useTableColumnDefs'

function GridTableV2Inner<T>(props: GridTableV2Props<T>, customRef: ForwardedRef<AgGridReact>) {
  const {
    idKey = 'id',
    className,
    title,
    radius,
    rowData = undefined,
    useColumnNo = false,
    isColumnNumPinned = false,
    columnDefs,
    gridOptions,
    rowSelection = 'multiple',
    rowSelectable,
    rowStartIndex = 0,
    totalCount: _totalCount = 0,
    useColumnFilter,
    showTableHeader = true,
    noRowsOverlayComponentParams,
    headerLeftActions,
    onSelectionChanged,
    onCellValueChanged,
    summaryRowData,
    pinnedBottomRowData,
    renderToolbar,
    onRowDoubleClicked,
    gridKey,
    legacyStorageKey,
    ...rest
  } = props

  const $ref = useRef<AgGridReact>(null)
  const savedColumns = useTableColumnDefs({ columnDefs, gridKey, legacyStorageKey })
  const [checkedNum, setCheckedNum] = useCheckedNum()
  const [totalCount, setTotalCount] = useState(_totalCount)
  const [searchText, setSearchText] = useState('')
  const [doubleClickedRowIndex, setDoubleClickedRowIndex] = useState<number | null>(null)

  const useFilterModel = useRef(true)
  const filterModelRef = useRef<Record<string, unknown> | null>(null)

  const columns = useMemo(() => {
    const copied = cloneDeep(savedColumns.columnDefs) || []

    if (useColumnNo) {
      copied.unshift({
        headerName: 'No',
        headerClass: 'moms-index-col-header',
        cellRenderer: (params: ICellRendererParams) => rowStartIndex + (params.node?.rowIndex ?? 0) + 1,
        width: 80,
        pinned: isColumnNumPinned && 'left',
      })
    }

    if (rowSelectable) {
      copied.unshift({
        pinned: true,
        width: 36,
        resizable: false,
        sortable: false,
        checkboxSelection: true,
        headerCheckboxSelection: rowSelection === 'multiple',
        showDisabledCheckboxes: true,
        headerClass: 'moms-checkbox-col-header',
        cellClass: 'moms-checkbox-col-cell',
      })
    }

    return copied.map((col) => {
      const cellClass: string[] = typeof col.cellClass === 'string' ? [col.cellClass] : Array.isArray(col.cellClass) ? col.cellClass : []

      if (useColumnFilter) {
        col.filter = 'agSetColumnFilter'
      }

      col.cellClass = cellClass.join(' ')
      return col
    })
  }, [savedColumns.columnDefs, rowSelection, useColumnFilter, rowSelectable, rowStartIndex, useColumnNo, isColumnNumPinned])

  const onFilterChanged = useCallback((e: FilterChangedEvent<T>) => {
    // 외부 페이지네이션 사용 시 필터 모델 저장
    if (useFilterModel.current) {
      filterModelRef.current = e.api.getFilterModel()
    }
    // 내부 필터링 시 totalCount 업데이트
    setTotalCount(e.api.getDisplayedRowCount())
  }, [])

  const onFilterTextBoxChanged = useCallback((v: string) => {
    setSearchText(v)
    $ref.current?.api?.setQuickFilter(v)
  }, [])

  const onGetRowId = useMemo(() => {
    if (rowSelectable) {
      return (params: GetRowIdParams<T>) => {
        const rowId = params.data?.[idKey as keyof T]
        return rowId ? rowId.toString() : 'moms-row-id'
      }
    }
  }, [idKey, rowSelectable])

  const handleSelectionChanged = useCallback(
    (params: SelectionChangedEvent) => {
      if (onSelectionChanged) onSelectionChanged(params)

      const selectedRows = params.api.getSelectedRows()
      const selectedRowsCount = selectedRows.length
      setCheckedNum(selectedRowsCount)

      // 셀 편집 중에는 redrawRows 가 편집 중인 행 DOM 을 파괴해 그리드가 비어 보임 — 편집 중이면 건너뜀
      // (인라인 편집 시 setSelected → selectionChanged 가 stopEditing 도중 발생하는 경로 방어)
      if (params.api.getEditingCells().length === 0) {
        params.api.redrawRows()
      }
    },
    [onSelectionChanged],
  )

  const handleCellValueChanged = useCallback(
    (params: CellValueChangedEvent) => {
      if (['edit', 'paste'].includes(params.source || '') && params.oldValue !== params.newValue) {
        params.node.setSelected(true)

        // 한글인 경우 엔터로 바로 stop editing 처리가 안되는 ag-grid 버그가 있어서 직접 처리.
        if (typeof params.rowIndex === 'number') {
          params.api.stopEditing()
          params.api.setFocusedCell(params.rowIndex, params.column)
        }

        onCellValueChanged?.(params as CellValueChangedEvent<T>)
      }
    },
    [onCellValueChanged],
  )

  const handleRowDoubleClicked = useCallback(
    (params: RowDoubleClickedEvent<T>) => {
      if (onRowDoubleClicked === undefined || params.node?.rowPinned) {
        return
      }

      const rowIndex = params.rowIndex ?? params.node?.rowIndex
      if (typeof rowIndex === 'number') {
        flushSync(() => {
          setDoubleClickedRowIndex(rowIndex)
        })
        if (params.api.getEditingCells().length === 0) {
          params.api.redrawRows()
        }
      }

      onRowDoubleClicked(params)
    },
    [onRowDoubleClicked],
  )

  const mergedGetRowStyle = useCallback(
    (params: RowClassParams<T>): RowStyle | undefined => {
      // 고정 행의 rowIndex 는 고정 영역 안 순번이라 데이터 첫 행(0)과 겹친다 — 강조 대상에서 뺀다
      if (params.node?.rowPinned) return undefined

      const rowIndex = params.rowIndex ?? params.node?.rowIndex

      if (typeof rowIndex === 'number' && doubleClickedRowIndex === rowIndex) {
        return {
          backgroundColor: tokens.layerDesktop['bg-accent-low'],
        }
      }
    },
    [doubleClickedRowIndex],
  )

  useEffect(() => {
    if ($ref.current && customRef) (customRef as MutableRefObject<AgGridReact>).current = $ref.current
  }, [])

  // columnDefs 변경 시 ag-grid에 강제 적용
  useEffect(() => {
    if ($ref.current?.api && columns.length > 0) {
      $ref.current.api.setColumnDefs(columns)
    }
  }, [columns])

  useEffect(() => {
    setTotalCount(_totalCount)
  }, [_totalCount])

  useEffect(() => {
    if (filterModelRef.current) {
      useFilterModel.current = false
      $ref.current?.api.setFilterModel(filterModelRef.current)
      setTimeout(() => {
        useFilterModel.current = true
      }, 0)
    }
  }, [rowData])

  // rowData가 변경되면 하이라이트 초기화
  useEffect(() => {
    setDoubleClickedRowIndex(null)
  }, [rowData])

  // 툴바 렌더링
  const toolbarContent = useMemo(() => {
    if (renderToolbar) {
      return renderToolbar({
        SearchBar,
        searchData: {
          value: searchText,
          onChange: onFilterTextBoxChanged,
        },
        Divider,
        ExportButton,
        UploadButton,
        DeleteButton,
        AddButton,
        Pagination,
      })
    }

    return null
  }, [renderToolbar, searchText, onFilterTextBoxChanged])

  return (
    <GridTableV2Wrap $radius={radius} className={className}>
      {/* 타이틀 영역 */}
      {title && <GridTitleWrap>{title}</GridTitleWrap>}

      {/* 상단 카운트 & 페이지네이션 영역 */}
      {showTableHeader && (
        <GridTableHeaderWrap>
          <GridTableHeaderLeft>
            {checkedNum > 0 ? (
              <>
                <Typography name="base-text-m" colorToken="fg">
                  선택
                </Typography>
                <Typography name="base-text-m" colorToken="layerDesktop-fg-accent">
                  {checkedNum}
                </Typography>
                <Typography name="base-text-m" colorToken="fg-lower">
                  /
                </Typography>
                <Typography name="base-text-m" colorToken="fg-lower">
                  {totalCount}
                </Typography>
                {headerLeftActions}
              </>
            ) : (
              <>
                <Typography name="base-text-m" colorToken="fg">
                  전체
                </Typography>
                <Typography name="base-text-m" colorToken="fg">
                  {totalCount}
                </Typography>
                {headerLeftActions}
              </>
            )}
          </GridTableHeaderLeft>

          <TableToolbarWrap>{toolbarContent}</TableToolbarWrap>
        </GridTableHeaderWrap>
      )}

      {/* 테이블 영역 */}
      <AgGridReactWrapV2 className="ag-theme-balham">
        <AgGridReact<T>
          ref={$ref}
          {...agGridOptions}
          rowSelection={rowSelection}
          gridOptions={{
            ...defaultGridOptions,
            ...gridOptions,
            getRowId: onGetRowId,
          }}
          noRowsOverlayComponent={NoRowsOverlayComponent}
          noRowsOverlayComponentParams={noRowsOverlayComponentParams}
          defaultColDef={defaultColDef}
          rowData={rowData}
          columnDefs={columns}
          pinnedTopRowData={summaryRowData ? [summaryRowData] : undefined}
          pinnedBottomRowData={pinnedBottomRowData}
          onSelectionChanged={handleSelectionChanged}
          onCellValueChanged={handleCellValueChanged}
          onFilterChanged={onFilterChanged}
          onRowDoubleClicked={handleRowDoubleClicked}
          getRowStyle={mergedGetRowStyle}
          {...rest}
          {...savedColumns.gridProps}
        />
      </AgGridReactWrapV2>
    </GridTableV2Wrap>
  )
}

const GridTableV2 = forwardRef(GridTableV2Inner) as <T>(
  props: GridTableV2Props<T> & { ref?: ForwardedRef<AgGridReact> },
) => ReturnType<typeof GridTableV2Inner>

export default GridTableV2
